import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { Button, Badge, Spinner, Card, Row, Col } from 'react-bootstrap';
import { PageTitle } from '../../../../_metronic/layout/core';
import { KTCard, KTCardBody, KTIcon } from '../../../../_metronic/helpers';
import { AssetCategory, Asset } from '../types/assetTypes';
import { get_single_asset } from '../api/assetsApi';
import AssetDownloadModal from './AssetDownloadModal';
import EditAssetFileModal from './EditAssetFileModal';
import UpdateLicenseModal from './UpdateLicenseModal';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';

const AssetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const assetId = id ? parseInt(id, 10) : 0;
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showEditFileModal, setShowEditFileModal] = useState(false);
  const [showUpdateLicenseModal, setShowUpdateLicenseModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<any>(null);
  const [assetForDownload, setAssetForDownload] = useState<Asset | null>(null);
  
  // Permission checks
  const canViewAsset = Boolean(permissionCheck('upload-view'));
  const canDownloadAsset = Boolean(permissionCheck('download-view'));
  const canUpdateAsset = Boolean(permissionCheck('upload-update'));

  const { data, isLoading, error } = useQuery(
    ['asset', assetId],
    () => get_single_asset(assetId),
    {
      enabled: !!assetId,
      refetchOnWindowFocus: false,
    }
  );

  const getCategoryLabel = (categoryName: string): string => {
    return categoryName || 'N/A';
  };

  const getCategoryBadgeVariant = (categoryName: string): string => {
    const name = (categoryName || '').toLowerCase();
    if (name.includes('video template')) return 'primary';
    if (name.includes('stock video')) return 'success';
    if (name.includes('audio')) return 'warning';
    if (name.includes('graphic')) return 'info';
    return 'secondary';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to construct full URL from file path
  const getThumbnailUrl = (filePath: string | null | undefined): string | null => {
    if (!filePath) return null;
    
    // If already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // Construct full URL from base URL
    let apiBaseUrl = process.env.REACT_APP_API_URL || '';
    // Remove /api suffix if present, but keep the base domain
    apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    // Ensure base URL doesn't end with slash
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    
    // Handle file path - remove leading slash if present, then add it back
    const cleanPath = filePath.replace(/^\//, '');
    
    return `${apiBaseUrl}/${cleanPath}`;
  };

  // Helper function to construct full URL for license file path
  const getLicenseFileUrl = (filePath: string | null | undefined): string | null => {
    if (!filePath) return null;
    
    // If already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // Construct full URL from base URL
    let apiBaseUrl = process.env.REACT_APP_API_URL || '';
    // Remove /api suffix if present, but keep the base domain
    apiBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    // Ensure base URL doesn't end with slash
    apiBaseUrl = apiBaseUrl.replace(/\/$/, '');
    
    // Handle file path - remove leading slash if present
    const cleanPath = filePath.replace(/^\//, '');
    
    // URL encode the filename and add /licenses/ prefix
    const encodedFileName = encodeURIComponent(cleanPath);
    
    return `${apiBaseUrl}/licenses/${encodedFileName}`;
  };

  // Get upload data from API response
  const upload = data?.data?.upload_data || data?.upload_data || null;

  // Get all thumbnails with full URLs
  const allThumbnails = upload?.thumbnails?.map((thumb: any) => ({
    id: thumb.id,
    file_path: thumb.file_path,
    is_primary: thumb.is_primary === 1 || thumb.is_primary === true,
    url: getThumbnailUrl(thumb.file_path)
  })) || [];

  // Set primary thumbnail as default selected
  useEffect(() => {
    if (allThumbnails.length > 0) {
      const primaryIndex = allThumbnails.findIndex((t: any) => t.is_primary);
      if (primaryIndex >= 0) {
        setSelectedThumbnailIndex(primaryIndex);
      }
    }
  }, [allThumbnails.length]);

  if (isLoading) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Asset Details</PageTitle>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3">Loading asset details...</div>
        </div>
      </>
    );
  }

  if (!canViewAsset) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Asset Details</PageTitle>
        <KTCard>
          <KTCardBody>
            <div className="alert alert-warning">
              You do not have permission to view assets.
            </div>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/digital-assets')}
              className="d-flex align-items-center"
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName="arrow-left" className="me-2" />
              <span>Back to Assets</span>
            </Button>
          </KTCardBody>
        </KTCard>
      </>
    );
  }

  if (error || !upload) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>Asset Details</PageTitle>
        <KTCard>
          <KTCardBody>
            <div className="alert alert-danger">
              {error ? 'Failed to load asset details. Please try again.' : 'Asset not found.'}
            </div>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/digital-assets')}
              className="d-flex align-items-center"
              style={{ cursor: 'pointer' }}
            >
              <KTIcon iconName="arrow-left" className="me-2" />
              <span>Back to Assets</span>
            </Button>
          </KTCardBody>
        </KTCard>
      </>
    );
  }

  const selectedThumbnail = allThumbnails[selectedThumbnailIndex];

  return (
    <>
      <PageTitle breadcrumbs={[]}>Asset Details</PageTitle>
      
      <div className="d-flex justify-content-between align-items-center mb-5">
        <Button 
          variant="secondary" 
          onClick={() => navigate('/digital-assets')}
          className="d-flex align-items-center"
          style={{ cursor: 'pointer' }}
        >
          <KTIcon iconName="arrow-left" className="me-2" />
          <span>Back to Assets</span>
        </Button>
      </div>

      <div className="row g-5">
        {/* Left Column - Thumbnails */}
        <div className="col-lg-4">
          <KTCard>
            <KTCardBody>
              {/* Main Thumbnail Display */}
              {selectedThumbnail?.url ? (
                <div className="mb-4">
                  <img
                    src={selectedThumbnail.url}
                    alt={upload.upload_name}
                    className="img-fluid rounded"
                    style={{ width: '100%', height: 'auto', maxHeight: '400px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  {selectedThumbnail.is_primary && (
                    <Badge bg="primary" className="mt-2">Primary Thumbnail</Badge>
                  )}
                </div>
              ) : (
                <div 
                  className="d-flex align-items-center justify-content-center bg-light rounded mb-4"
                  style={{ height: '300px' }}
                >
                  <KTIcon iconName="file" className="fs-3x text-muted" />
                </div>
              )}

              {/* Thumbnail Gallery */}
              {allThumbnails.length > 1 && (
                <div>
                  <h6 className="mb-3">All Thumbnails</h6>
                  <div className="row g-2">
                    {allThumbnails.map((thumb: any, index: number) => (
                      <div key={thumb.id} className="col-4">
                        <div
                          className={`thumbnail-preview rounded border p-1 cursor-pointer ${
                            selectedThumbnailIndex === index ? 'border-primary border-2' : 'border-secondary'
                          }`}
                          onClick={() => setSelectedThumbnailIndex(index)}
                          style={{ cursor: 'pointer' }}
                        >
                          {thumb.url ? (
                            <img
                              src={thumb.url}
                              alt={`Thumbnail ${index + 1}`}
                              className="img-fluid rounded"
                              style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center bg-light" style={{ height: '80px' }}>
                              <KTIcon iconName="file" className="text-muted" />
                            </div>
                          )}
                          {thumb.is_primary && (
                            <Badge bg="primary" className="position-absolute top-0 start-0 m-1" style={{ fontSize: '0.65rem' }}>
                              P
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </KTCardBody>
          </KTCard>
        </div>

        {/* Right Column - Details */}
        <div className="col-lg-8">
          {/* Basic Information */}
          <KTCard className="mb-5">
            <KTCardBody>
              <div className="d-flex justify-content-between align-items-start mb-4">
                <div>
                  <h2 className="mb-2">{upload.upload_name || 'Untitled Asset'}</h2>
                  <Badge bg={getCategoryBadgeVariant(upload.category?.category_name || '')} className="mb-2">
                    {getCategoryLabel(upload.category?.category_name || '')}
                  </Badge>
                </div>
              </div>

              {upload.description && (
                <div className="mb-4">
                  <h5 className="mb-2">
                    <KTIcon iconName="document" className="me-2" />
                    Description
                  </h5>
                  <p className="text-muted">{upload.description}</p>
                </div>
              )}

              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <strong>
                      <KTIcon iconName="calendar" className="me-2" />
                      Created:
                    </strong>
                    <div className="text-muted">{formatDate(upload.created_at)}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>
                      <KTIcon iconName="calendar-tick" className="me-2" />
                      Updated:
                    </strong>
                    <div className="text-muted">{formatDate(upload.updated_at)}</div>
                  </div>
                </Col>
                {upload.source_url && (
                  <Col md={12}>
                    <div className="mb-3">
                      <strong>
                        <KTIcon iconName="link" className="me-2" />
                        Source URL:
                      </strong>
                      <div>
                        <a href={upload.source_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                          {upload.source_url}
                          <KTIcon iconName="arrow-top-right" className="ms-1 fs-7" />
                        </a>
                      </div>
                    </div>
                  </Col>
                )}
                {upload.asset_file_path && (
                  <Col md={12}>
                    <div className="mb-3">
                      <strong>
                        <KTIcon iconName="file-down" className="me-2" />
                        Asset File:
                      </strong>
                      <div className="mt-2 d-flex gap-2">
                        {canDownloadAsset && (
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => {
                              // Create Asset object from upload data for the modal
                              const asset: Asset = {
                                id: upload.id,
                                name: upload.upload_name || '',
                                category: upload.category?.category_name 
                                  ? (upload.category.category_name.toLowerCase().includes('video template') 
                                      ? AssetCategory.VIDEO_TEMPLATES
                                      : upload.category.category_name.toLowerCase().includes('stock video')
                                      ? AssetCategory.STOCK_VIDEOS
                                      : upload.category.category_name.toLowerCase().includes('audio')
                                      ? AssetCategory.AUDIOS
                                      : AssetCategory.GRAPHICS)
                                  : AssetCategory.GRAPHICS,
                                description: upload.description || '',
                                tags: upload.tags?.map((t: any) => t.tag_title || '') || [],
                                thumbnails: allThumbnails.map((t: any) => t.url || '').filter(Boolean),
                                zip_file_url: upload.asset_file_path || '',
                                licenses: [],
                                metadata: {
                                  size: 0,
                                  file_type: 'zip',
                                  mime_type: 'application/zip',
                                },
                                source_platform: upload.platform?.platform_name as any,
                                source_url: upload.source_url || '',
                                uploaded_at: upload.created_at || '',
                                status: 'completed',
                              };
                              setAssetForDownload(asset);
                              setShowDownloadModal(true);
                            }}
                          >
                            <KTIcon iconName="file-down" className="me-2" />
                            Download & Associate Asset
                          </Button>
                        )}
                        {canUpdateAsset && (
                          <Button 
                            variant="warning" 
                            size="sm"
                            onClick={() => {
                              setShowEditFileModal(true);
                            }}
                          >
                            <KTIcon iconName="pencil" className="me-2" />
                            Replace File
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </KTCardBody>
          </KTCard>

          {/* Category & Subcategories */}
          <KTCard className="mb-5">
            <KTCardBody>
              <h5 className="mb-3">
                <KTIcon iconName="category" className="me-2" />
                Category & Subcategories
              </h5>
              <div className="mb-3">
                <strong>Category:</strong>
                <div className="text-muted">{upload.category?.category_name || 'N/A'}</div>
              </div>
              {upload.subcategories && upload.subcategories.length > 0 ? (
                <div>
                  <strong>Subcategories:</strong>
                  <div className="mt-2">
                    {upload.subcategories.map((subcat: any) => (
                      <Badge key={subcat.id} bg="info" className="me-2 mb-2">
                        {subcat.subcategory_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-muted">No subcategories</div>
              )}
            </KTCardBody>
          </KTCard>

          {/* Tags */}
          {upload.tags && upload.tags.length > 0 && (
            <KTCard className="mb-5">
              <KTCardBody>
                <h5 className="mb-3">
                  <KTIcon iconName="tag" className="me-2" />
                  Tags
                </h5>
                <div>
                  {upload.tags.map((tag: any) => (
                    <Badge key={tag.id} bg="secondary" className="me-2 mb-2">
                      {tag.tag_title}
                    </Badge>
                  ))}
                </div>
              </KTCardBody>
            </KTCard>
          )}

          {/* Platform */}
          {upload.platform && (
            <KTCard className="mb-5">
              <KTCardBody>
                <h5 className="mb-3">
                  <KTIcon iconName="abstract-26" className="me-2" />
                  Source Platform
                </h5>
                <div className="mb-2">
                  <strong>Platform Name:</strong>
                  <div className="text-muted">{upload.platform.platform_name || 'N/A'}</div>
                </div>
                {upload.platform.platform_url && (
                  <div className="mb-2">
                    <strong>Platform URL:</strong>
                    <div>
                      <a href={upload.platform.platform_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                        {upload.platform.platform_url}
                        <KTIcon iconName="arrow-top-right" className="ms-1 fs-7" />
                      </a>
                    </div>
                  </div>
                )}
                {upload.platform.description && (
                  <div>
                    <strong>Description:</strong>
                    <div className="text-muted">{upload.platform.description}</div>
                  </div>
                )}
              </KTCardBody>
            </KTCard>
          )}

          {/* Project Hierarchy */}
          {upload.project_hierarchy && upload.project_hierarchy.length > 0 && (
            <KTCard className="mb-5">
              <KTCardBody>
                <h5 className="mb-4">
                  <KTIcon iconName="briefcase" className="me-2" />
                  Project Hierarchy
                </h5>
                {upload.project_hierarchy.map((project: any) => (
                  <Card key={project.id} className="mb-3">
                    <Card.Body>
                      <div className="mb-3">
                        <h6 className="text-primary mb-2">
                          <KTIcon iconName="folder" className="me-2" />
                          {project.project_name}
                        </h6>
                        {project.description && (
                          <p className="text-muted small mb-0">{project.description}</p>
                        )}
                      </div>
                      
                      {project.subprojects && project.subprojects.length > 0 && (
                        <div>
                          <strong className="d-block mb-2">Subprojects:</strong>
                          {project.subprojects.map((subproject: any) => (
                            <Card key={subproject.id} className="mb-2 bg-light">
                              <Card.Body className="p-3">
                                <div className="mb-2">
                                  <strong>
                                    <KTIcon iconName="file-up" className="me-2" />
                                    {subproject.subproject_name}
                                  </strong>
                                </div>
                                {subproject.description && (
                                  <div className="text-muted small mb-2">{subproject.description}</div>
                                )}
                                
                                {subproject.licenses && subproject.licenses.length > 0 && (
                                  <div>
                                    <strong className="d-block mb-2">Licenses:</strong>
                                    {subproject.licenses.map((license: any, idx: number) => (
                                      <div key={idx} className="mb-2 p-2 bg-white rounded border">
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div className="flex-grow-1">
                                            {license.license_file_path && (
                                              <div className="mb-1">
                                                <strong>File:</strong>
                                                {getLicenseFileUrl(license.license_file_path) ? (
                                                  <a 
                                                    href={getLicenseFileUrl(license.license_file_path) || '#'} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    download
                                                    className="text-primary ms-2 text-decoration-none"
                                                  >
                                                    {license.license_file_path}
                                                    <KTIcon iconName="arrow-top-right" className="ms-1 fs-7" />
                                                  </a>
                                                ) : (
                                                  <span className="text-muted ms-2">{license.license_file_path}</span>
                                                )}
                                              </div>
                                            )}
                                            {license.license_date && (
                                              <div className="mb-1">
                                                <strong>Date:</strong>
                                                <span className="text-muted ms-2">{new Date(license.license_date).toLocaleDateString()}</span>
                                              </div>
                                            )}
                                            {license.description && (
                                              <div>
                                                <strong>Description:</strong>
                                                <span className="text-muted ms-2">{license.description}</span>
                                              </div>
                                            )}
                                          </div>
                                          {canUpdateAsset && (
                                            <div className="ms-2">
                                              <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => {
                                                  setSelectedLicense({
                                                    id: license.id,
                                                    license_id: license.id,
                                                    subproject_id: subproject.id,
                                                    project_id: project.id,
                                                    license_date: license.license_date,
                                                    license_text: license.description || license.license_text,
                                                    description: license.description,
                                                    license_file_path: license.license_file_path
                                                  });
                                                  setShowUpdateLicenseModal(true);
                                                }}
                                                title="Update License"
                                              >
                                                <KTIcon iconName="pencil" className="fs-7" />
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </KTCardBody>
            </KTCard>
          )}
        </div>
      </div>

      {/* Download Modal */}
      <AssetDownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setAssetForDownload(null);
        }}
        asset={assetForDownload}
      />

      {/* Edit File Modal */}
      {upload && (
        <EditAssetFileModal
          show={showEditFileModal}
          onHide={() => setShowEditFileModal(false)}
          uploadId={String(upload.id)}
          oldFilePath={upload.asset_file_path || null}
          onSuccess={() => {
            // Refetch asset data after successful upload
            queryClient.invalidateQueries(['asset', assetId]);
          }}
        />
      )}

      {/* Update License Modal */}
      {selectedLicense && (
        <UpdateLicenseModal
          show={showUpdateLicenseModal}
          onHide={() => {
            setShowUpdateLicenseModal(false);
            setSelectedLicense(null);
          }}
          license={selectedLicense}
          onSuccess={() => {
            // Refetch asset data after successful update
            queryClient.invalidateQueries(['asset', assetId]);
            setSelectedLicense(null);
          }}
        />
      )}
    </>
  );
};

export default AssetDetailPage;
